import { HttpErrorResponse } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
	MatLegacyDialog as MatDialog,
	MatLegacyDialogConfig as MatDialogConfig,
} from "@angular/material/legacy-dialog";
import { clampDay } from "@app/_helpers/date-fns";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { isBreakRuleError, isWorkingHoursError } from "@app/_helpers/is-error-object";
import { type EvaluateWorkingHoursOptions, evaluateWorkingHours } from "@app/_helpers/rulebreakUtil";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import { jsonStorage } from "@app/_helpers/storage";
import {
	coerceTimeFormat,
	distinctUntilChangedJson,
	fromRxValue,
	resolveRawArgs,
	roundUpMinute,
	WithOptional,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { CustomValidators } from "@app/_validators/custom-validators";
import {
	ComeAndGoCreateDialogComponent,
	WorkingHoursCreateData,
} from "@app/components/come-and-go-create-dialog/come-and-go-create-dialog.component";
import {
	ComeAndGoRulebreakFixDialogComponent,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-rulebreak-dialog/come-and-go-rulebreak-dialog.component";
import { SONNER_DEFAULT_CONFIG, SONNER_ERROR_TOAST_DURATION_MS } from "@app/config/sonner";
import { extract } from "@app/core";
import {
	type BeforeStopResult,
	ComegoRecordingRulebreakService,
} from "@app/services/comego-recording-rulebreak.service";
import { CreateUndoService } from "@app/services/create-undo/create-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import { EntityStore } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ApplicationSettingsQuery,
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	Logger,
	MyTimesQuery,
	MyTimesService,
	Project,
	ProjectsQuery,
	Tag,
	Task,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import { NotifierService } from "angular-notifier";
import { CalendarEvent } from "calendar-utils";
import { intervalToDuration } from "date-fns";
import {
	addDays,
	addHours,
	addMinutes,
	addSeconds,
	clamp,
	endOfDay,
	format as formatString,
	isBefore,
	isSameDay,
	isSameMinute,
	parse as parseFromString,
	roundToNearestMinutes,
	startOfDay,
	startOfMinute,
	startOfToday,
	subHours,
	subMinutes,
} from "date-fns/esm";
import sorter from "fluent-sorter";
import produce from "immer";
import { flow, merge } from "lodash-es";
import { MediaObserver } from "ngx-flexible-layout";
import { ExternalToast, toast } from "ngx-sonner";
import { BehaviorSubject, combineLatest, firstValueFrom, Subject } from "rxjs";
import {
	auditTime,
	debounceTime,
	distinctUntilChanged,
	filter,
	finalize,
	map,
	skip,
	startWith,
	switchMap,
	tap,
} from "rxjs/operators";
import {
	TimeTrackCreateData,
	TimeTrackerCalendarCreateDialogComponent,
} from "../time-tracker-calendar-create-dialog/time-tracker-calendar-create-dialog.component";
import { TimeTrackerCalendarUpdateDialogComponent } from "../time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";
import { SelectMode } from "./record-toolbar.component";
@UntilDestroy()
@ObserveFormGroupErrors()
@Injectable({
	providedIn: "root",
})
export class RecordToolbarService {
	private lastValidStartTime: string | null = null;
	private lastValidEndTime: string | null = null;

	get configInputMode(): "range" | "duration" {
		const inputMode = (this.appSettings.getValue()?.inputMode as string) ?? "range";
		const validModes = ["range", "duration"];
		return validModes.includes(inputMode) ? (inputMode as any) : "range";
	}
	readonly runningTime = fromRxValue(
		this.myTimesQuery.selectAll({ filterBy: (x) => !x.end && !x["timeType"] && !x["timeGaps"], limitTo: 1 }).pipe(
			debounceTimeAfterFirst(100),
			map((times) => times[0]),
			tap((x) => {
				log.debug("runningTime", x);
			})
		)
	);

	group = new FormGroup({
		name: new FormControl("", [
			Validators.minLength(3),
			(ctrl) => {
				const user = this.userSettingsQuery.getValue();
				if (user.workspace?.settings?.requireName) {
					return Validators.required(ctrl);
				}
				return null;
			},
		]),
		project: new FormControl<any>(
			this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault === true, limitTo: 1 })[0],
			[
				Validators.required,
				(ctrl) => {
					const value = ctrl.value as Project;
					const user = this.userSettingsQuery.getValue();
					if (
						user.workspace?.settings?.requireProject &&
						user.settings?.captureManualMode &&
						value &&
						typeof value === "object" &&
						value.useAsDefault
					) {
						return {
							required: true,
						};
					}
					return null;
				},
			]
		),
		task: new FormControl(null, (ctrl) => {
			// const value = ctrl.value as Task;
			const user = this.userSettingsQuery.getValue();
			if (user.workspace?.settings?.requireTask && user.settings?.captureManualMode && !ctrl.value?.id) {
				return {
					required: true,
				};
			}
			return null;
		}),
		billable: new FormControl(false),
		tags: new FormControl<any[]>([]),
		time: new FormGroup(
			{
				start: new FormControl("00:00", [CustomValidators.validDateFormat("HH:mm")]),
				end: new FormControl("00:00", [CustomValidators.validDateFormat("HH:mm")]),
				recordStart: new FormControl<Date>(null, [CustomValidators.validDate]),
				duration: new FormControl("00:00", [CustomValidators.validDuration()]),
			},
			[CustomValidators.timeDiffCheck("start", "end")]
		),
		ref: new FormControl<string>(null),
		inputMode: new FormControl<"range" | "duration">(this.configInputMode),
	});
	groupError(controlName: string) {
		return useFormErrorObservable(this)(
			controlName,
			() => this.group.get(controlName) as UntypedFormControl,
			{
				required: (error, ctrl) => {
					if (controlName === "name") return { content: "errors.time.name", args: {} };
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					return {
						content: "errors.required",
						args: { field: controlName },
					};
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: controlName, length: error.requiredLength },
				}),
			},
			(key) => this.translate.instant(key),
			{
				initialValidate: true,
			}
		);
	}
	readonly onClear = new Subject<void>();
	clear() {
		this.onClear.next();
		this.resetAll();
	}
	private _isLoading = new BehaviorSubject<boolean>(false);
	readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
	get isLoading() {
		return this._isLoading.getValue();
	}
	set isLoading(val: boolean) {
		this._isLoading.next(val);
	}
	reset() {
		this.group.patchValue({
			time: {
				start: `${new Date().getHours().toString()?.padStart(2, "0")}:00`,
				end: `${new Date().getHours().toString()?.padStart(2, "0")}:00`,
				duration: `${new Date().getHours().toString()?.padStart(2, "0")}:00`,
			},
		});
	}
	resetProject(name?: boolean) {
		let data: any = {
			project: this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault === true })[0],
			task: null,
			ref: null,
		};
		if (name) data.name = "";
		this.group.patchValue(data);
	}
	resetEntites() {
		this.group.patchValue({
			name: "",
			project: this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault === true })[0],
			task: null,
			billable: false,
			tags: [],
			ref: null,
		});
		this.group.markAsUntouched();
	}
	resetDate(date?: Date) {
		this.group.patchValue({
			time: {
				recordStart: date ?? null,
			},
		});
	}
	resetAll() {
		const now = new Date();
		this.group.patchValue({
			name: "",
			project: this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault === true })[0],
			task: null,
			billable: false,
			tags: [],
			time: {
				start: `${now.getHours().toString()?.padStart(2, "0")}:00`,
				end: `${now.getHours().toString()?.padStart(2, "0")}:00`,
				duration: `${new Date().getHours().toString()?.padStart(2, "0")}:00`,
			},
			ref: null,
		});
		this.group.markAsUntouched();
	}
	constructor(
		private projectsQuery: ProjectsQuery,
		private media: MediaObserver,
		private dialog: MatDialog,
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private translate: TranslateService,
		private notifier: NotifierService,
		private appSettings: ApplicationSettingsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private comegoRecordingRulebreak: ComegoRecordingRulebreakService,
		private createUndoService: CreateUndoService,
		private mixedTimesSyncService: MixedTimesSyncService,
		private clock: ClockService
	) {}
	private initialized = false;

	private getLastBookedTimeEntry(selectedDate: Date): Time | null {
		// First try to get the last entry on the selected date
		const lastBookedTimeEntryOnDate = this.myTimesQuery.getAll({
			filterBy: (time) => !!time.start && !!time.end && isSameDay(new Date(time.start), selectedDate),
			sortBy: (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
			limitTo: 1,
		});

		if (lastBookedTimeEntryOnDate && lastBookedTimeEntryOnDate.length > 0) {
			return lastBookedTimeEntryOnDate[0];
		}

		// Fallback to the last entry overall if no entry on the selected date
		const lastBookedTimeEntry = this.myTimesQuery.getAll({
			filterBy: (time) => !!time.start && !!time.end,
			sortBy: (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
			limitTo: 1,
		});

		if (!lastBookedTimeEntry || lastBookedTimeEntry.length === 0) {
			return null;
		}

		return lastBookedTimeEntry[0];
	}

	private getCompletedTimesOnSameDay(selectedDate: Date, excludeEntryId?: string): Time[] {
		return this.myTimesQuery.getAll({
			filterBy: (time) =>
				!!time.start &&
				!!time.end &&
				isSameDay(new Date(time.start), selectedDate) &&
				(!excludeEntryId || time.id !== excludeEntryId),
		});
	}

	/**
	 * Fits a suggested range into the next free slot on the selected day.
	 * Uses merged intervals and a single forward scan to avoid restart loops.
	 */
	private resolveNonOverlappingRange(
		selectedDate: Date,
		start: Date,
		end: Date,
		lastDurationSeconds: number,
		excludeEntryId?: string
	): { start: Date; end: Date } {
		const dayStart = startOfDay(selectedDate);
		const dayEnd = endOfDay(selectedDate);
		const clampToSelectedDay = (value: Date): Date => clamp(value, { start: dayStart, end: dayEnd });

		const durationSeconds = Math.max(0, Math.floor(lastDurationSeconds || 0));
		let startTs = clampToSelectedDay(start).getTime();

		const intervals = this.getCompletedTimesOnSameDay(selectedDate, excludeEntryId)
			.map((t) => ({ start: new Date(t.start).getTime(), end: new Date(t.end).getTime() }))
			.filter((t) => Number.isFinite(t.start) && Number.isFinite(t.end) && t.end > t.start)
			.sort((a, b) => a.start - b.start)
			.reduce<{ start: number; end: number }[]>((acc, current) => {
				const prev = acc[acc.length - 1];
				if (!prev) {
					acc.push({ start: current.start, end: current.end });
					return acc;
				}
				if (current.start <= prev.end) {
					prev.end = Math.max(prev.end, current.end);
					return acc;
				}
				acc.push({ start: current.start, end: current.end });
				return acc;
			}, []);

		for (const interval of intervals) {
			if (startTs >= interval.start && startTs < interval.end) {
				startTs = interval.end;
				continue;
			}
			if (startTs < interval.start) {
				break;
			}
		}

		startTs = clampToSelectedDay(new Date(startTs)).getTime();
		let endTs = clampToSelectedDay(addSeconds(new Date(startTs), durationSeconds)).getTime();

		const nextInterval = intervals.find((interval) => interval.start > startTs);
		if (nextInterval && endTs > nextInterval.start) {
			endTs = nextInterval.start;
		}

		endTs = clampToSelectedDay(new Date(endTs)).getTime();
		if (endTs < startTs) {
			endTs = startTs;
		}

		return { start: new Date(startTs), end: new Date(endTs) };
	}

	private watchForSelectedDateEntryChanges(selectedDate: Date) {
		// Watch for changes to entries on the selected date only
		return this.myTimesQuery
			.selectAll({
				filterBy: (time) => !!time.start && !!time.end && isSameDay(new Date(time.start), selectedDate),
			})
			.pipe(
				map((selectedDateEntries) => {
					// Return the most recent entry on the selected date
					if (selectedDateEntries.length > 0) {
						const sorted = selectedDateEntries.sort(sorter((x) => new Date(x.start), "desc"));
						return sorted[0];
					}
					return null;
				}),
				distinctUntilChanged((prev, curr) => {
					// Compare by ID, start time, and end time to detect meaningful changes
					if (!prev && !curr) return true;
					if (!prev || !curr) return false;
					return prev.id === curr.id && prev.start === curr.start && prev.end === curr.end;
				})
			);
	}

	setDefaultTime() {
		const user = this.userSettingsQuery.getValue();
		const timesMode = user.workspace?.settings?.timesMode;
		let inputMode: any = this.configInputMode;
		if (timesMode === "duration") inputMode = "duration";
		const now = new Date();
		const today = startOfDay(now);

		let lastDurationSeconds = 3600; // Default 1 hour
		let start = roundToNearestMinutes(
			user.workspace.settings?.blockTimes?.futureMode != "none"
				? subHours(now.getTime(), 1)
				: subMinutes(new Date(now.getTime()), 15),
			{
				nearestTo: 15,
			}
		); // Default start time
		let selectedDate = this.group.get("time")?.get("recordStart")?.value || now;
		// After midnight, roll initial date suggestion to today so new entries default to the current day
		if (selectedDate && isBefore(startOfDay(selectedDate), today)) {
			selectedDate = now;
			this.group.patchValue(
				{
					time: {
						recordStart: today,
					},
				},
				{ emitEvent: false }
			);
		}

		// Get the last booked time entry (tries selected date first, then falls back to overall last entry)
		const lastBookedTimeEntry = this.getLastBookedTimeEntry(selectedDate);

		if (lastBookedTimeEntry) {
			lastDurationSeconds = lastBookedTimeEntry.timeDiff || 3600;

			const lastEnd = formatString(new Date(lastBookedTimeEntry.end), "HH:mm");

			// Convert duration seconds to HH:mm format
			const durationHHMM = formatString(addSeconds(startOfDay(new Date()), lastDurationSeconds), "HH:mm");

			// If the last entry ended at 23:59, add 1 minute to the duration if the last digit of the duration is 4 or 9
			if (lastEnd === "23:59") {
				const lastDigit = parseInt(durationHHMM.split(":")[1]) % 10;
				if (lastDigit === 4 || lastDigit === 9) {
					lastDurationSeconds += 60;
				}
			}

			// Check if we should use the last entry's end time
			if (isSameDay(new Date(lastBookedTimeEntry.end), selectedDate)) {
				// Use the last entry's end time as start time suggestion
				start = new Date(lastBookedTimeEntry.end);

				// If the last entry ended at 23:59 and is from the same day, proceed to the next day
				const endTime = formatString(start, "HH:mm");
				if (endTime === "23:59") {
					// Move to next day and start from beginning
					const lastEntryDate = new Date(lastBookedTimeEntry.end);
					const nextDay = addDays(lastEntryDate, 1);
					start = startOfDay(nextDay);
					selectedDate = nextDay; // Update selectedDate for end time calculation

					// Also update the selected date to the next day
					this.group.patchValue(
						{
							time: {
								recordStart: nextDay,
							},
						},
						{ emitEvent: false }
					);
				}
			} else {
				// Check if the selected date is the day after the last entry
				if (isSameDay(addDays(new Date(lastBookedTimeEntry.end), 1), selectedDate) && lastEnd === "23:59") {
					// Start from 00:00 on the day after the last entry
					start = startOfDay(selectedDate);
				}
			}
		}
		let end = addSeconds(start.getTime(), lastDurationSeconds);

		if (end > endOfDay(start.getTime())) {
			end = endOfDay(start.getTime());
		}

		const resolved = this.resolveNonOverlappingRange(selectedDate, start, end, lastDurationSeconds);
		start = resolved.start;
		end = resolved.end;

		let duration = ((d) => (~~d.hours * 60 + ~~d.minutes) * 60)(intervalToDuration({ start, end }));

		this.group.patchValue({
			time: {
				start: formatString(start, "HH:mm"),
				end: formatString(end, "HH:mm"),
				duration: formatString(addSeconds(startOfDay(start.getTime()), duration), "HH:mm"),
			},
			inputMode,
		});

		// Update last valid times
		this.lastValidStartTime = formatString(start, "HH:mm");
		this.lastValidEndTime = formatString(end, "HH:mm");
	}
	initialize() {
		this.setDefaultTime(); // Initialize always
		if (!this.initialized) {
			this.initialized = true;
			// Watch for changes to the selected date and react to entry changes on that date
			// This ensures suggestions are updated when entries are created/modified on the selected date (including past dates)
			this.group
				.get("time")
				?.get("recordStart")
				?.valueChanges.pipe(
					untilDestroyed(this),
					startWith(this.group.get("time")?.get("recordStart")?.value || new Date()),
					distinctUntilChanged((prev, curr) => {
						// Only react to date changes, not time changes
						if (!prev && !curr) return true;
						if (!prev || !curr) return false;
						return isSameDay(prev, curr);
					}),
					switchMap((selectedDate) => {
						// Watch for entry changes on the selected date
						return this.watchForSelectedDateEntryChanges(selectedDate || new Date()).pipe(
							skip(1) // Skip the initial value to avoid unnecessary reload
						);
					})
				)
				.subscribe((newestEntry) => {
					// Update suggestions whenever an entry on the selected date changes
					if (newestEntry) {
						this.setDefaultTime();
					}
				});

			this.group.valueChanges.subscribe(({ inputMode }) => {
				this.appSettings.updateByKey("inputMode", inputMode);
			});
			(this.group.controls.time as UntypedFormGroup).valueChanges
				.pipe(
					untilDestroyed(this),
					map(({ start, end, recordStart, duration }) => {
						// Convert end time "00:00" to "23:59" (end of day)
						if (end === "00:00") {
							(this.group.controls.time as UntypedFormGroup).patchValue(
								{ end: "23:59" },
								{ emitEvent: true }
							);
							end = "23:59";
						}
						return { start, end, recordStart, duration };
					}),
					distinctUntilChangedJson(({ start, end }) => ({ start, end })),
					filter((x) => x.start !== "00:00" && x.end !== "00:00")
				)
				.subscribe(({ start, end, recordStart: date }) => {
					const refDate = new Date(date);
					let range;
					// Change end time when new start time is set and end is in the past
					if (
						this.lastValidStartTime &&
						this.lastValidEndTime &&
						start !== this.lastValidStartTime &&
						end < start
					) {
						// Calculate the duration from the last valid times
						const lastValidStart = parseFromString(this.lastValidStartTime, "HH:mm", refDate);
						const lastValidEnd = parseFromString(this.lastValidEndTime, "HH:mm", refDate);
						const lastValidDuration = intervalToDuration({
							start: lastValidStart,
							end: lastValidEnd,
						});

						// Convert duration to minutes and add to the new start time
						const durationInMinutes =
							(lastValidDuration.hours || 0) * 60 + (lastValidDuration.minutes || 0);
						const newStart = clampDay(refDate)(parseFromString(start, "HH:mm", refDate));

						range = {
							start: newStart,
							end: clampDay(refDate)(
								addMinutes(newStart, durationInMinutes > 0 ? durationInMinutes : 30)
							),
						};
					} else {
						range = {
							start: clampDay(refDate)(parseFromString(start, "HH:mm", refDate)),
							end: clampDay(refDate)(parseFromString(end, "HH:mm", refDate)),
						};
					}

					this.lastValidStartTime =
						formatString(range.start, "HH:mm") || formatString(startOfDay(range.start), "HH:mm");
					this.lastValidEndTime =
						formatString(range.end, "HH:mm") || formatString(endOfDay(range.end), "HH:mm");
					const duration = intervalToDuration({
						...range,
					});
					(this.group.controls.time as UntypedFormGroup).patchValue({
						start: this.lastValidStartTime,
						end: this.lastValidEndTime,
						duration: formatString(
							parseFromString(`${~~duration.hours}:${~~duration.minutes}`, "HH:mm", new Date()),
							"HH:mm"
						),
					});
				});
			combineLatest([
				this.group.controls.name.valueChanges.pipe<string>(debounceTime(1000)),
				this.group.valueChanges.pipe(debounceTime(250)),
				this.myTimesQuery
					.selectAll({ filterBy: (x) => !x.end })
					.pipe(map((x) => (x?.length > 0 ? x[0] : null))),
			])
				.pipe(
					debounceTime(250),
					filter(([name, value, time]) => this.group.valid && time && !time.end && value?.project),
					filter(() => !this.userSettingsQuery.getValue().settings.captureManualMode && !this.isLoading)
				)
				.pipe(
					distinctUntilChanged(([oldName, oldValue, oldTime], [newName, newValue, newTime]) => {
						const time = newTime;
						if (!newName) newName = time.name;
						return (
							newName === time.name &&
							newValue.project.id === time.project.id &&
							newValue.task?.id === time.task?.id &&
							JSON.stringify((newValue.tags ?? []).map((x: Tag) => x.id).sort()) ===
								JSON.stringify((time.tags ?? []).map((x: Tag) => x.id).sort()) &&
							newValue.billable === time.billable
						);
					}),
					skip(1)
					// skipWhile(([name, value, time]) => this.group.invalid)
				)
				.pipe(
					switchMap(([name, value, time]) => {
						this.isLoading = true;
						return firstValueFrom(
							this.myTimesService.update(
								produce(time, (draft) => {
									draft.name = name;
									if (value.project) draft.project = value.project;
									draft.task = value.task;
									draft.billable = value.billable;
									draft.tags = value.tags ?? [];
									return draft;
								})
							)
						).finally(() => {
							this.isLoading = false;
						});
					})
				)
				.subscribe();
			this.userSettingsQuery
				.select()
				.pipe(
					untilDestroyed(this),
					startWith(this.userSettingsQuery.getValue()),
					map((x) => x?.workspace?.settings),
					distinctUntilChangedJson()
				)
				.subscribe(() => {
					this.group.updateValueAndValidity();
				});
			this.reloadRunningRecording().then((time) => {
				log.debug("last active recording", time);
			});
		}
	}
	private translateArgs<T extends Object>(value?: T): T {
		if (!value || typeof value !== "object") return {} as T;
		return Object.entries(value)
			.filter((val) => val?.length && val[0] && val[1])
			.reduce((acc, [key, value]) => {
				if (key && value) acc[key] = this.translate.instant(value);
				return acc;
			}, {} as T);
	}
	notifyError<T1 extends { [key: string]: any }>(body: string, options?: ExternalToast, args?: T1, rawArgs?: T1) {
		const messageArgs = merge(this.translateArgs(args || {}) || {}, rawArgs || {});
		toast.error(
			null,
			merge(
				{},
				{ ...SONNER_DEFAULT_CONFIG },
				{
					duration: SONNER_ERROR_TOAST_DURATION_MS,
					closeButton: true,
					dismissible: true,
					description: this.translate.instant(body, messageArgs),
				} as ExternalToast,
				options
			)
		);
	}
	notifyInfo(body: string, options?: ExternalToast, args?: { [key: string]: any }) {
		toast.info(
			null,
			merge({}, { ...SONNER_DEFAULT_CONFIG }, options, {
				description: this.translate.instant(body, this.translateArgs(args || {})),
			})
		);
	}
	notifyWarn(body: string, options?: ExternalToast, args?: { [key: string]: any }) {
		toast.warning(
			null,
			merge(
				{},
				{ ...SONNER_DEFAULT_CONFIG },
				{
					duration: 30000,
					closeButton: true,
					description: this.translate.instant(body, this.translateArgs(args || {})),
				} as ExternalToast,
				options
			)
		);
	}
	notifySuccess(body: string, options?: ExternalToast, args?: { [key: string]: any }) {
		toast.success(
			null,
			merge(
				{},
				{ ...SONNER_DEFAULT_CONFIG },
				{ description: this.translate.instant(body, this.translateArgs(args || {})) } as ExternalToast,
				options
			)
		);
	}
	hasPendingInput: boolean;
	//#region isPlaying
	private _isPlaying: boolean;
	silentTogglePlaying(playState?: boolean) {
		this._isPlaying = playState !== undefined ? playState : !this.isPlaying;
	}
	public isPlayingChange = new EventEmitter<boolean>();
	get isPlaying() {
		return this._isPlaying;
	}
	set isPlaying(val: boolean) {
		this._isPlaying = val;
		this.isPlayingChange.emit(val);
	}
	//#endregion
	//#region description
	private _taskName: string;
	public taskNameChange = new EventEmitter<string>();
	get TaskName() {
		return this._taskName;
	}
	set TaskName(val: string) {
		this._taskName = val;
		this.taskNameChange.emit(val);
		log.debug("taskname: ", this._taskName);
	}
	setTaskName(val: string) {
		this._taskName = val;
	}
	//#endregion
	//#region startTime
	private startTime: Date;
	public StartTimeChange = new EventEmitter<Date>();
	get StartTime() {
		return this.startTime;
	}
	set StartTime(val: Date) {
		this.startTime = val;
		this.StartTimeChange.emit(val);
	}
	//#endregion
	//#region startTime
	private _manualStartTime: Date = new Date();
	public ManualStartTimeChange = new EventEmitter<Date>();
	get ManualStartTime() {
		return this._manualStartTime;
	}
	set ManualStartTime(val: Date) {
		this._manualStartTime = val as Date;
		this.ManualStartTimeChange.emit(val);
	}
	private _manualEndTime: Date = new Date();
	public ManualEndTimeChange = new EventEmitter<Date>();
	get ManualEndTime() {
		return this._manualEndTime;
	}
	set ManualEndTime(val: Date) {
		this._manualEndTime = val as Date;
		this.ManualEndTimeChange.emit(val);
	}

	timeRangeSetter = new EventEmitter<{ start: Date; end: Date }>(true);
	//#endregion
	//#region selectedMode
	private _selectedRecordMode = new BehaviorSubject<SelectMode>(SelectMode.Timer);
	readonly selectedRecordMode$ = this._selectedRecordMode.asObservable().pipe(distinctUntilChanged());
	get selectedRecordMode() {
		return this._selectedRecordMode.getValue();
	}
	set selectedRecordMode(val: SelectMode) {
		this._selectedRecordMode.next(val);
	}
	private _events = new BehaviorSubject<string>(null);
	readonly events$ = this._events.asObservable().pipe(auditTime(100));
	get events() {
		return this._events.getValue();
	}
	set events(val: string) {
		this._events.next(val);
	}

	//#endregion
	//#region prj
	private _selectedProject = new BehaviorSubject<Project>(null);
	readonly selectedProject$ = this._selectedProject.asObservable().pipe(distinctUntilChanged());
	public selectedProjectChange = new EventEmitter<Project>();
	get selectedProject() {
		return this._selectedProject.getValue();
	}
	set selectedProject(val: Project) {
		this._selectedProject.next(val);
		this.selectedProjectChange.emit(val);
	}
	//#endregion
	private _selectedTags = new BehaviorSubject<Tag[]>([]);
	readonly selectedTags$ = this._selectedTags.asObservable().pipe(distinctUntilChanged());
	get selectedTags() {
		return this._selectedTags.getValue();
	}
	set selectedTags(val: Tag[]) {
		this._selectedTags.next(val);
	}
	private _billed = new BehaviorSubject<boolean>(false);
	readonly billed$ = this._billed.asObservable().pipe(distinctUntilChanged());
	get billed() {
		return this._billed.getValue();
	}
	set billed(val: boolean) {
		this._billed.next(val);
	}
	async loadAutomodeData() {
		const time = (await this.myTimesService.getLatestRecordings(1))[0];
		if (!time) return null;
		(this.myTimesQuery.__store__ as EntityStore).upsert(time.id, time);
		this.selectedRecordMode = SelectMode.Timer;
		this.group.patchValue({
			name: time.name || "",
			project: time.project?.id
				? (this.projectsQuery.getEntity(time.project.id) ?? time.project)
				: this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault })[0],
			task: time.task,
			billable: !!time?.billable,
			tags: time.tags ?? [],
			time: {
				start: formatString(new Date(time.start), "HH:mm"),
				end: formatString(new Date(time.start), "HH:mm"),
			},
		});
		this.isPlaying = true;
		return time;
	}
	/** Re-reads the currently running recording and applies it to the record controls. */
	async reloadRunningRecording() {
		const time = await this.loadAutomodeData().catch((err) => {
			log.error(err);
			return null;
		});
		this.isPlaying = !!time;
		if (time) this.selectedRecordMode = SelectMode.Timer;
		return time;
	}
	async saveActiveRecording() {
		const running = await this.myTimesService.getLatestRecordings(1).then((x) => x[0]);
		return await new Promise<Time>((resolve, reject) => {
			if (running && !running.end) {
				this.isLoading = true;
				return this.myTimesService
					.update({
						...running,
						end: startOfMinute(new Date()).toISOString(),
					})
					.toPromise()
					.then(() => {
						this.isLoading = false;
						this.notifySuccess("success.created");
						resolve(running);
					})
					.catch(reject);
			}
			return resolve(null);
		});
	}
	async continueFromTask(entry: Task) {
		return this.continueRecording({
			name: null,
			project: entry.project as any,
			billable: false,
			task: entry,
		} as any);
	}
	async continueRecording(entry: Time) {
		const project = this.projectsQuery.getEntity(entry.project.id);
		return this.saveActiveRecording()
			.then((prevTime) => {
				if (prevTime) this.notifySuccess("record-toolbar.saved-prevtime");
				this.isLoading = true;
				this.selectedRecordMode = SelectMode.Timer;
				this.StartTime = startOfMinute(new Date());
				this.group.patchValue({
					project,
					task: entry.task,
					name: entry.name,
					billable: !!entry.billable,
					tags: entry.tags || [],
					time: {
						recordStart: this.StartTime,
					},
				});
				return this.myTimesService
					.add({
						name: entry.name,
						start: startOfMinute(new Date()),
						project: entry.project,
						// @ts-ignore
						task: entry.task,
						billable: !!entry.billable,
						outlookCalenderReference: entry.outlookCalenderReference,
						tags: entry.tags || ([] as any),
						end: null,
					})
					.toPromise();
			})
			.then(async (time) => {
				this.silentTogglePlaying(true);
				this.isLoading = false;
				this.notifySuccess("success.recordingContinued");
				const created = Array.isArray(time) ? time[0] : time;
				if (created) {
					await this.maybeDualStartWorkingHours(created);
				}
				return time;
			})
			.catch((err) => {
				this.isLoading = false;
				log.error(err);

				this.handleError(err);
				return Promise.reject(err);
			});
	}
	async copyRecording(entry: Time, options: Partial<{ recordMode: SelectMode }> = {}) {
		const project = this.projectsQuery.getEntity(entry.project.id) ?? { ...entry.project, client: entry.client };
		this.selectedRecordMode = options?.recordMode ?? SelectMode.Manual;
		this.events = "COPY_ENTRY";
		this.group.patchValue({
			project,
			task: entry.task,
			name: entry.name,
			billable: !!entry.billable,
			tags: entry.tags || [],
			// @ts-ignore
			inputMode: entry.inputMode,
		});
	}
	private _saveLoading = new BehaviorSubject<boolean>(false);
	readonly saveLoading$ = this._saveLoading.asObservable().pipe(distinctUntilChanged());
	get saveLoading() {
		return this._saveLoading.getValue();
	}
	set saveLoading(val: boolean) {
		this._saveLoading.next(val);
	}

	async save(data: Partial<Time & { inputMode: "duration" | "range" }>): Promise<Time | undefined> {
		this.saveLoading = true;
		const start = new Date(data.start),
			end = new Date(data.end),
			duration = data.timeDiff;
		if (start > end) {
			this.notifyError("record-toolbar.start-lt-end");
			this.saveLoading = false;
			return;
		}
		if (!data.project?.id) {
			this.saveLoading = false;
			throw new Error(extract("errors.task.not-added"));
		}
		const mode = this.userSettingsQuery.getValue()?.workspace.settings?.timesMode ?? "range";
		const inputMode = data.inputMode;
		const taskId = data.task?.id;
		const timeFields =
			mode === "range_optional"
				? data.inputMode === "duration"
					? {
							start: startOfDay(start.getTime()),
							end: startOfDay(end.getTime()),
							timeDiff: duration,
						}
					: {
							start: start.toISOString(),
							end: end.toISOString(),
						}
				: mode === "duration"
					? { start: startOfDay(start.getTime()), end: startOfDay(end.getTime()), timeDiff: duration }
					: {
							start: start.toISOString(),
							end: end.toISOString(),
						};
		const user = this.userSettingsQuery.getValue();
		const draft = {
			...timeFields,
			inputMode,
			user: { id: user.id },
			project: data.project,
		};
		const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(draft);
		if (!confirmed) {
			this.saveLoading = false;
			return undefined;
		}
		return new Promise<Time[]>((resolve, reject) => {
			this.myTimesService
				.add({
					project: { id: data.project.id },
					name: data.name,
					// @ts-ignore
					task: data.task,
					billable: !!data.billable,
					outlookCalenderReference: data.outlookCalenderReference,
					inputMode,
					...timeFields,
					tags: (data.tags?.filter(({ id, name }) => id && name) ?? []) as any,
				})
				.pipe(
					finalize(() => {
						this.saveLoading = false;
					})
				)
				.subscribe({
					next: (...args) => resolve(...args),
					error: (...args) => reject(...args),
				});
		})
			.then(async ([x]) => {
				log.debug(x);
				const user = this.userSettingsQuery.getValue();
				let mode = user.workspace.settings?.timesMode ?? "range";
				if (mode === "range_optional") mode = "range";
				if (user.settings.captureManualMode === true && mode === "range") {
					// Use the duration of the entry that was just saved
					const savedDurationSeconds = Math.max(
						0,
						(new Date(x.end).getTime() - new Date(x.start).getTime()) / 1000
					);
					let newStart = new Date(x.end),
						newEnd = addSeconds(new Date(x.end), savedDurationSeconds);
					//  TODO: clearify how the new time suggestions impacts the future mode
					// 	newEnd = user.workspace.settings?.blockTimes?.futureMode === "none" ? addSeconds(new Date(x.end), savedDurationSeconds) : new Date(x.end);
					// if (user.workspace.settings?.blockTimes?.futureMode != "none") {
					// 	if (differenceInHours(new Date(), newEnd) > 0) newEnd = addSeconds(newEnd, savedDurationSeconds);
					// }

					// If the saved entry ended at 23:59, advance to next day
					const savedEndTime = formatString(new Date(x.end), "HH:mm");
					let recordStartDate = new Date(x.start); // Use the date from the saved entry
					if (savedEndTime === "23:59") {
						// Move to next day and start from beginning
						recordStartDate = addDays(recordStartDate, 1);
						newStart = startOfDay(recordStartDate);
						newEnd = addSeconds(newStart, savedDurationSeconds);

						// Only add 1 minute if the end time ends with 4 or 9 minutes
						const endMinutes = newEnd.getMinutes();
						const lastDigit = endMinutes % 10;
						if (lastDigit === 4 || lastDigit === 9) {
							newEnd = addMinutes(newEnd, 1);
						}
					}

					const newStartTimeStr = formatString(newStart, "HH:mm");
					const newEndTimeStr = formatString(
						isSameDay(newEnd, newStart) ? newEnd : endOfDay(newEnd),
						"HH:mm"
					);

					const parsedStart = parseFromString(newStartTimeStr, "HH:mm", recordStartDate);
					const parsedEnd = parseFromString(newEndTimeStr, "HH:mm", recordStartDate);

					const resolved = this.resolveNonOverlappingRange(
						recordStartDate,
						parsedStart,
						parsedEnd,
						savedDurationSeconds,
						x.id
					);
					const resolvedStartTimeStr = formatString(resolved.start, "HH:mm");
					const resolvedEndTimeStr = formatString(resolved.end, "HH:mm");

					this.group.patchValue({
						time: {
							start: resolvedStartTimeStr,
							end: resolvedEndTimeStr,
							recordStart: recordStartDate,
						},
					});

					// Update last valid times
					this.lastValidStartTime = resolvedStartTimeStr;
					this.lastValidEndTime = resolvedEndTimeStr;
				}
				this.handleSuccess(x);
				if (taskId && x.task?.id != taskId) pushError("errors.times.only_assignedusers_can_book_on_task");
				await this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(x);
				this.createUndoService.offerSingleUndo({ entry: x });
				this.resetEntites();
				return x;
			})
			.catch((err) => {
				log.error(err);
				this.saveLoading = false;
				this.handleError(err);
				this.revertOnError(data);
				return undefined;
			});
	}
	revertOnError(data: Partial<Time>) {
		this.group.patchValue({
			name: data.name,
			project: data.project,
			task: data.task,
			billable: data.billable,
			tags: data.tags,
			time: {
				start: formatString(new Date(data.start), "HH:mm"),
				end: formatString(new Date(data.end), "HH:mm"),
				duration: formatString(addSeconds(startOfToday(), data.timeDiff), "HH:mm"),
			},
		});
	}
	openCreateDialog(data?: Partial<typeof this.group.value>): Promise<Time | Time[] | null | undefined> {
		this.setDefaultTime();

		// Get the updated form values after setDefaultTime
		const timeFormValues = this.group.get("time")?.value;
		let recordStartDate = timeFormValues?.recordStart || new Date();
		// Ensure we use today when opening the dialog after midnight (stale date from previous day)
		if (recordStartDate && isBefore(startOfDay(recordStartDate), startOfDay(new Date()))) {
			recordStartDate = new Date();
		}
		const currentDt = data?.time?.recordStart || recordStartDate;

		// Convert form time strings to Date objects
		const startTime = timeFormValues?.start
			? coerceTimeFormat(timeFormValues.start, currentDt)
			: (data?.time?.start && coerceTimeFormat(data?.time.start, currentDt)) || currentDt;

		const endTime = timeFormValues?.end
			? coerceTimeFormat(timeFormValues.end, currentDt)
			: (data?.time?.end && coerceTimeFormat(data?.time.end, currentDt)) || currentDt;

		return new Promise<Time | Time[] | null | undefined>((resolve) => {
			return this.dialog
				.open(TimeTrackerCalendarCreateDialogComponent, {
					data: <TimeTrackCreateData>{
						title: data?.name || this.group.value.name,
						timeDiff: 0,
						start: startTime,
						end: endTime,
						date: startOfDay(currentDt),
						project: data?.project,
						task: data?.task,
						tags: data?.tags,
						billable: data?.billable,
						outlookRefId: data?.ref,
					},
				})
				.afterClosed()
				.subscribe(resolve);
		});
	}
	async openUpdateDialog(time: Time, forceUpdate: boolean = false, options?: MatDialogConfig) {
		return await firstValueFrom<Time[]>(
			this.dialog
				.open(TimeTrackerCalendarUpdateDialogComponent, {
					...(options || {}),
					data: <CalendarEvent<{ time: Time; oldStart?: Date; oldEnd?: Date }>>{
						title: time.name,
						start: new Date(time.start),
						end: new Date(time.end),
						meta: { time, forceUpdate },
					},
				})
				.afterClosed()
		);
	}
	async startCheckin(data: Parameters<ComegoService["add"]>[0]) {
		// const user = this.userSettingsQuery.getValue();
		// const lastCheckin: ComegoTime[] = await this.comegoService.adapter
		//   .get(environment.serverUrl + `/get/comego?$filter=user__id eq '${user.id}'&$orderby=end desc&$top=1`)
		//   .toPromise();
		// let lastEnd = lastCheckin?.length && DateTime.fromJSDate(new Date(lastCheckin[0].end));
		// const now = DateTime.fromJSDate(new Date());
		// if (lastEnd && !now.hasSame(lastEnd, "day") && now.diff(lastEnd, 'hours').hours < 11) {
		//   pushError('Please make sure you are considering your resting time.');
		// }
		// console.log('last', lastCheckin);
		return await this.comegoService
			.add(data)
			.then(this.handleWorkingHourSuccess.bind(this))
			.catch(this.handleWorkingHourError);
	}
	async startRecord(data?: Partial<{ name: string; project: Project; task: Task }>) {
		const project = data?.project || this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault })[0];
		if (!project) {
			const time = await this.myTimesService
				.getLatestRecordings(1)
				.then((d) => d?.[0])
				.catch((err) => {
					log.error(err);
					return null;
				});
			if (time) {
				await this.stopRecord({ requireUpdate: true });
			}
		}
		return await firstValueFrom(
			this.myTimesService.add({
				name: data?.name,
				project,
				// @ts-ignore
				task: data?.task?.project?.id === project?.id ? data?.task : undefined,
				start: roundToNearestMinutes(new Date(), { nearestTo: 1 }),
				billable: false,
				end: null,
				recordType: "timer",
			})
		)
			.then((time) => time || [])
			.then(async ([time]) => {
				if (time) {
					this.group.patchValue({
						project: time.project,
						task: time.task || null,
						name: time.name,
						tags: time.tags,
						billable: time.billable,
						ref: time.outlookCalenderReference,
						inputMode: time["inputMode"],
					});
					await this.maybeDualStartWorkingHours(time);
				}
				return time;
			})
			.catch((err) => {
				this.clearPairedWorkingHoursRecording();
				this.handleError(err);
				this.revertOnError(data);
				return Promise.reject(err);
			});
	}
	/** start/end/inputMode a running project time gets when stopped, per workspace timesMode. */
	resolveRecordingStopRange(startIso: string, now: Date = new Date()) {
		const mode = this.userSettingsQuery.getValue().workspace.settings?.timesMode ?? "range";
		const start = mode === "duration" ? startOfDay(new Date(startIso)) : startOfMinute(new Date(startIso));
		const end =
			mode === "duration"
				? roundUpMinute(addSeconds(start.getTime(), (now.getTime() - Date.parse(startIso)) / 1000))
				: roundToNearestMinutes(now, { nearestTo: 1 });
		return { start, end, inputMode: mode === "duration" ? ("duration" as const) : ("range" as const) };
	}

	async stopRecord(
		options?: Partial<{ showUpdate: boolean; checkServiceControls: boolean; requireUpdate: boolean }>
	) {
		let time = await this.myTimesService.getLatestRecordings(1).then((x) => x?.[0]);
		if (!time) return;
		if (!options) options = {};
		if (time && new Date(time.start).getDate() !== new Date().getDate()) {
			options.requireUpdate = true;
		}
		if (time && options?.requireUpdate) {
			// Ensure the update dialog reflects the latest selected values from the record controls
			// (otherwise it may show stale name/project/task when the user changed them right before stopping).
			if (options?.checkServiceControls === true) {
				const value = this.group.value;
				time = produce(time, (draft: WithOptional<Time>) => {
					draft.name = value.name;
					draft.project = value.project;
					draft.billable = value.billable;
					// If the user changed the project but didn't pick a matching task,
					// don't carry over a task from another project.
					const task = value.task;
					const projectId = value.project?.id;
					const taskProjectId = task?.project?.id;
					draft.task = task && projectId && taskProjectId && taskProjectId !== projectId ? null : task;
					if (value.ref) draft.outlookCalenderReference = value.ref;
				});
			}
			const newTimes = await this.openUpdateDialog(time, false);
			if (!newTimes) return;
			else if (newTimes.every?.((x) => !!x.end)) {
				this.resetEntites();
				await this.afterProjectRecordingClosed(newTimes);
				return newTimes;
			} else {
				time = newTimes?.[0];
				// overnight split rows were left untouched, the dialog kept the recording running on purpose
				if (time && !time.end && new Date(time.start).getDate() !== new Date().getDate()) return newTimes;
			}
		} else if (!time) {
			(this.myTimesQuery.__store__ as MyTimesStore).remove((t) => !t.end);
			// await this.myTimesService.getLatestRecordings(1).then((x) => x?.[0]);
			return;
		}
		const taskId = time.task?.id;
		const closedDraft = produce(time, (draft: WithOptional<Time>) => {
			const { start, end, inputMode } = this.resolveRecordingStopRange(draft.start);
			draft.inputMode = inputMode;
			draft.start = start.toISOString();
			draft.end = end.toISOString();
			// When we already went through the update dialog (requireUpdate),
			// the dialog returns the latest name/project/task.
			// Don't overwrite those values again from the toolbar/widget form state.
			if (options?.checkServiceControls === true && !options?.requireUpdate) {
				const value = this.group.value;
				draft.name = value.name;
				draft.project = value.project;
				draft.billable = value.billable;
				// If the user changed the project but didn't pick a matching task,
				// don't carry over a task from another project.
				const task = value.task;
				const projectId = value.project?.id;
				const taskProjectId = task?.project?.id;
				draft.task = task && projectId && taskProjectId && taskProjectId !== projectId ? null : task;
				if (value.ref) draft.outlookCalenderReference = value.ref;
			}
			draft.recordType = "timer";
		});
		const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(closedDraft);
		if (!confirmed) return;
		return await firstValueFrom(this.myTimesService.update(closedDraft))
			.then(async ([x]) => {
				this.handleSuccess(x);
				if (taskId && x.task?.id !== taskId)
					this.notifyInfo("errors.times.only_assignedusers_can_book_on_task");
				if (x.hasTimeOverlap && !options?.requireUpdate) {
					const updated = await this.openUpdateDialog(x, true);
					await this.afterProjectRecordingClosed(updated ?? [x]);
					return updated;
				}
				this.notifySuccess("success.created");
				if (options?.showUpdate === false || options?.requireUpdate) {
					await this.afterProjectRecordingClosed([x]);
					return [x];
				}
				const updated = await this.openUpdateDialog(x, true);
				await this.afterProjectRecordingClosed(updated ?? [x]);
				return updated;
			})
			.catch((err) => {
				this.handleError(err);
				this.revertOnError(time);
				return Promise.reject(err);
			});
	}

	/**
	 * Discards a running project timer. If this session dual-started working hours,
	 * deletes that open come-go recording (without checking it out) and rebuilds the day.
	 */
	async discardRunningRecord(id: string) {
		const open = this.myTimesQuery.getEntity(id);
		// Read the pairing before clearing it: discarding the session drops the break the dual
		// start created to fill the gap (an extended break is left as-is).
		const gapFillPauseId = this.getPairedRecording()?.gapFillPauseId ?? null;
		const result = await firstValueFrom(this.myTimesService.delete({ id } as any));
		await this.clearDualStartedWorkingHoursRecording();
		await this.removeGapFillPause(gapFillPauseId);
		if (open?.start) {
			await this.mixedTimesSyncService.handleProjectTimeDelete(open);
		}
		return result;
	}

	/** Removes a break the dual start created to fill a gap; extended breaks are not tracked. */
	private async removeGapFillPause(gapFillPauseId: string | null | undefined): Promise<void> {
		if (!gapFillPauseId) return;
		await this.comegoService.delete({ id: gapFillPauseId }).catch(() => {
			// Already gone if the day was rebuilt first.
		});
	}

	private getOpenWorkRecordingForUser(userId: string | null | undefined): ComegoTime | null {
		if (!userId) return null;
		const times = this.comegoQuery.getAll({
			filterBy: (x: ComegoTime & { deleted?: boolean }) => x.user?.id === userId && !x.end && !x.deleted,
		}) as ComegoTime[];
		if (!times.length) return null;
		return times.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
	}

	private getOpenWorkingHoursRecording(): ComegoTime | null {
		return this.getOpenWorkRecordingForUser(this.userSettingsQuery.getValue()?.id);
	}

	private getPairedWorkingHoursStorageKey(): string | null {
		const user = this.userSettingsQuery.getValue();
		const userId = user?.id;
		const workspaceId = user?.workspace?.id ?? user?.defaultWorkspace;
		if (!userId || !workspaceId) return null;
		return `recordToolbar.pairedWh.${workspaceId}.${userId}`;
	}

	private getPairedRecording(): {
		workingHoursId: string;
		projectTimeId: string | null;
		gapFillPauseId: string | null;
	} | null {
		const key = this.getPairedWorkingHoursStorageKey();
		if (!key) return null;
		const value = jsonStorage[key] as
			| { workingHoursId?: string; projectTimeId?: string; gapFillPauseId?: string }
			| string
			| null;
		if (!value) return null;
		if (typeof value === "string") return { workingHoursId: value, projectTimeId: null, gapFillPauseId: null };
		if (!value.workingHoursId) return null;
		return {
			workingHoursId: value.workingHoursId,
			projectTimeId: value.projectTimeId ?? null,
			gapFillPauseId: value.gapFillPauseId ?? null,
		};
	}

	private getPairedWorkingHoursRecordingId(): string | null {
		return this.getPairedRecording()?.workingHoursId ?? null;
	}

	private setPairedWorkingHoursRecording(
		workingHoursId: string | null,
		projectTimeId?: string,
		gapFillPauseId?: string | null
	): void {
		const key = this.getPairedWorkingHoursStorageKey();
		if (!key) return;
		if (!workingHoursId) {
			localStorage.removeItem(key);
			return;
		}
		jsonStorage[key] = {
			workingHoursId,
			projectTimeId: projectTimeId ?? null,
			gapFillPauseId: gapFillPauseId ?? null,
		};
	}

	private clearPairedWorkingHoursRecording(): void {
		this.setPairedWorkingHoursRecording(null);
	}

	/**
	 * Dual-start paired open WH for the current (or given) project timer.
	 * Returns null when unpaired, WH is closed/missing, or projectTimeId mismatches.
	 */
	getPairedOpenWorkingHoursRecording(projectTimeId?: string | null): ComegoTime | null {
		const pair = this.getPairedRecording();
		if (!pair) return null;
		if (projectTimeId && pair.projectTimeId && pair.projectTimeId !== projectTimeId) return null;
		const wh = this.comegoQuery.getEntity(pair.workingHoursId) as (ComegoTime & { deleted?: boolean }) | undefined;
		if (!wh || wh.end || wh.deleted) return null;
		return wh;
	}

	/** userId:dayISO keys already fetched through ComegoService this session. */
	private readonly loadedViolationDays = new Set<string>();

	private async ensureComegoDayLoaded(day: Date, userId: string): Promise<void> {
		const key = `${userId}:${startOfDay(day).toISOString()}`;
		if (this.loadedViolationDays.has(key)) return;
		// Neighbour days are needed for the rest-between-working-days rule.
		const times = await this.comegoService
			.getBetween(startOfDay(addDays(day, -1)), endOfDay(addDays(day, 1)), userId)
			.catch((): ComegoTime[] => []);
		if (times?.length) (this.comegoQuery.__store__ as ComegoStore).upsertMany(times);
		this.loadedViolationDays.add(key);
	}

	/** True when any working-hours rule is violated for the user's comego times on `day`. */
	private dayHasWorkingHoursViolation(day: Date): boolean {
		const user = this.userSettingsQuery.getValue();
		const wh = user?.workspace?.schedules?.workspace?.workinghours;
		if (!wh || !user?.id) return false;

		const userId = user.id;
		const dayTimes = this.comegoQuery.getAll({
			filterBy: (t: ComegoTime & { deleted?: boolean }) =>
				t.user?.id === userId && !t.deleted && isSameDay(new Date(t.start), day),
		}) as ComegoTime[];
		if (!dayTimes.length) return false;

		const prevDay = addDays(day, -1);
		const nextDay = addDays(day, 1);
		const prevWork = this.comegoQuery.getAll({
			filterBy: (t: ComegoTime & { deleted?: boolean }) =>
				t.user?.id === userId && !t.deleted && t.type === "work" && isSameDay(new Date(t.start), prevDay),
			sortBy: (a, b) => new Date(b.end || new Date()).getTime() - new Date(a.end || new Date()).getTime(),
			limitTo: 1,
		})[0] as ComegoTime | undefined;
		const nextWork = this.comegoQuery.getAll({
			filterBy: (t: ComegoTime & { deleted?: boolean }) =>
				t.user?.id === userId && !t.deleted && t.type === "work" && isSameDay(new Date(t.start), nextDay),
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
			limitTo: 1,
		})[0] as ComegoTime | undefined;

		const violations = evaluateWorkingHours(dayTimes, wh, day, {
			prevDayItem: prevWork,
			nextDayItem: nextWork,
		} as EvaluateWorkingHoursOptions);
		return violations.length > 0;
	}

	/**
	 * True while a running PH timer is dual-start paired to an open WH whose day
	 * has any working-hours violation. Polled on a clock tick while active.
	 */
	readonly linkedWorkingHoursViolation$ = combineLatest([
		this.runningTime.asObservable(),
		this.comegoQuery.selectAll(),
		this.clock.now$,
	]).pipe(
		switchMap(async ([runningTime]) => {
			if (!runningTime?.id || runningTime.end) return false;
			const openWh = this.getPairedOpenWorkingHoursRecording(runningTime.id);
			if (!openWh) return false;
			const day = startOfDay(new Date(openWh.start));
			await this.ensureComegoDayLoaded(day, this.userSettingsQuery.getValue().id);
			return this.dayHasWorkingHoursViolation(day);
		}),
		distinctUntilChanged()
	);

	/** Shared with the come-and-go widgets: blocks WH check-in until the auto-created break ends. */
	readonly disabledUntil = createRxValueWithCache<string | null>("disableRecordingUntil", null);

	async maybeDualStartWorkingHours(projectTime?: Time): Promise<void> {
		const settings = this.userSettingsQuery.getValue().workspace?.settings;
		// Match mixed-times sync: WH must be enabled. Prefer allHours when present,
		// otherwise allow both hour types enabled independently.
		const mixedHoursEnabled =
			!!settings?.allHoursEnabled || (!!settings?.workinghoursEnabled && !!settings?.projecthoursEnabled);
		if (!mixedHoursEnabled) return;
		if (this.getOpenWorkingHoursRecording()) {
			// An existing WH recording is not owned by this project timer session.
			this.clearPairedWorkingHoursRecording();
			return;
		}
		const cuser = this.userSettingsQuery.getValue();
		const user = cuser.workspace.users.find((x) => x.id === cuser.id);
		if (!user) return;
		const start = roundToNearestMinutes(new Date(projectTime?.start ?? Date.now()), { nearestTo: 1 });
		// Mirror manual check-in: fill the gap after preceding work with a break, or extend a
		// preceding break up to the new work start. Best effort and silent (no success toast).
		const gapFillPauseId = await this.adjustPrecedingComegoForDualStart(start, cuser.id, user).catch((err) => {
			pushError(err);
			return null;
		});
		const checkinData = {
			name: null,
			start,
			end: null,
			type: "work",
			user,
		} as any;
		try {
			const times = await this.startCheckin(checkinData);
			const createdWh = times?.[0];
			if (createdWh?.id) {
				this.setPairedWorkingHoursRecording(createdWh.id, projectTime?.id, gapFillPauseId);
			} else {
				this.clearPairedWorkingHoursRecording();
			}
		} catch (err) {
			await this.handleDualStartWorkingHoursError(err as HttpErrorResponse);
		}
	}

	/**
	 * Adjusts the preceding come-and-go entry so the dual-started work row does not leave an
	 * unaccounted gap: a closed work block gets a break added up to the new start, a preceding
	 * break is extended up to it. Returns the id of a break it created (so a discard can remove
	 * it) or null when it extended an existing one or did nothing.
	 */
	private async adjustPrecedingComegoForDualStart(
		start: Date,
		userId: string,
		user: unknown
	): Promise<string | null> {
		await this.ensureComegoDayLoaded(startOfDay(start), userId);
		const previousEntry = (
			this.comegoQuery.getAll({
				filterBy: (x: ComegoTime & { deleted?: boolean }) =>
					x.user?.id === userId &&
					!!x.end &&
					!x.deleted &&
					(x.type === "work" || x.type === "pause") &&
					isSameDay(new Date(x.start), start) &&
					new Date(x.end).getTime() <= start.getTime(),
			}) as ComegoTime[]
		).sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
		if (!previousEntry?.end) return null;
		if (isSameMinute(new Date(previousEntry.end), start)) return null;
		if (previousEntry.type === "work") {
			const [created] = await this.comegoService.add({
				name: null,
				start: new Date(previousEntry.end),
				end: start,
				type: "pause",
				user,
			} as any);
			return created?.id ?? null;
		}
		if (previousEntry.type === "pause") {
			await this.comegoService.update({
				...previousEntry,
				end: start,
				user,
			} as any);
			return null;
		}
		return null;
	}

	/** The dual-started WH check-in is best effort: the project timer keeps running. */
	private async handleDualStartWorkingHoursError(err: HttpErrorResponse): Promise<void> {
		this.clearPairedWorkingHoursRecording();
		const error = err?.error;
		if (!isWorkingHoursError(error)) return;
		pushError(err);
		if (error.message !== "errors.times.comego.breakRuleNotice-until") return;
		const until = addMinutes(startOfMinute(new Date()), error.args?.value ?? 0);
		this.disabledUntil.update(formatString(until, "HH:mm"));
		// The 401 skipped the store write, so the server-created break is not in Akita yet.
		await this.comegoService.getFromDate(new Date()).catch((): null => null);
	}

	/** Deletes the dual-started open WH recording without checking it out as a closed entry. */
	private async clearDualStartedWorkingHoursRecording(): Promise<void> {
		const workingHoursId = this.getPairedWorkingHoursRecordingId();
		this.clearPairedWorkingHoursRecording();
		if (!workingHoursId) return;
		await this.comegoService.delete({ id: workingHoursId }).catch(() => {
			// Already removed if the day was rebuilt first (e.g. update dialog sync).
		});
	}

	/**
	 * Clears open WH work rows dual-started with the closed project times (same start minute).
	 * Needed when stopping another user's timer: localStorage pairing is only for the current user.
	 */
	private async clearDualStartedWorkingHoursForClosedProjectTimes(closed: Time[]): Promise<void> {
		const pairedId = this.getPairedWorkingHoursRecordingId();
		for (const time of closed) {
			const userId = time.user?.id ?? (time as Time & { userId?: string }).userId;
			if (!userId || !time.start) continue;
			await this.ensureComegoDayLoaded(startOfDay(new Date(time.start)), userId);
			const projectStartMs = startOfMinute(new Date(time.start)).getTime();
			const openWhs = this.comegoQuery.getAll({
				filterBy: (x: ComegoTime & { deleted?: boolean }) =>
					x.user?.id === userId &&
					!x.end &&
					!x.deleted &&
					x.type === "work" &&
					startOfMinute(new Date(x.start)).getTime() === projectStartMs,
			}) as ComegoTime[];
			for (const wh of openWhs) {
				if (!wh?.id) continue;
				await this.comegoService.delete({ id: wh.id }).catch(() => {
					// Already removed if pairing clear or day rebuild ran first.
				});
				if (pairedId && wh.id === pairedId) {
					this.clearPairedWorkingHoursRecording();
				}
			}
		}
	}

	/**
	 * When the dual-started WH recording is discarded, also delete the linked
	 * open project-hours recording and clear the pairing.
	 */
	async clearDualStartedProjectHoursRecording(workingHoursId: string): Promise<void> {
		const pair = this.getPairedRecording();
		if (!pair || pair.workingHoursId !== workingHoursId) return;
		this.clearPairedWorkingHoursRecording();
		await this.removeGapFillPause(pair.gapFillPauseId);
		const projectId =
			pair.projectTimeId ?? this.myTimesQuery.getAll({ filterBy: (t) => !t.end, limitTo: 1 })[0]?.id ?? null;
		if (!projectId) return;
		await firstValueFrom(this.myTimesService.delete({ id: projectId } as any)).catch(() => null);
		this.isPlaying = false;
		this.resetEntites();
	}

	/**
	 * After a dual-started WH recording is stopped.
	 * @param alsoStopProject true for checkout; false for pause/resume (release link only).
	 */
	async afterPairedWorkingHoursStopped(workingHoursId: string, alsoStopProject: boolean): Promise<void> {
		const pair = this.getPairedRecording();
		if (!pair || pair.workingHoursId !== workingHoursId) return;
		this.clearPairedWorkingHoursRecording();
		if (!alsoStopProject) return;
		const closed = await this.silentStopLinkedProjectTime(pair.projectTimeId);
		if (!closed) return;
		await this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(closed);
	}

	/** Closes the linked project timer without touching WH or opening the update dialog. */
	private async silentStopLinkedProjectTime(projectTimeId: string | null): Promise<Time | null> {
		const open =
			(projectTimeId && this.myTimesQuery.getEntity(projectTimeId)) ||
			this.myTimesQuery.getAll({ filterBy: (t) => !t.end, limitTo: 1 })[0];
		if (!open?.id || open.end) return null;
		const now = startOfMinute(new Date());
		const draft = produce(open, (d: WithOptional<Time>) => {
			d.end = now.toISOString();
			d.inputMode = "range";
		});
		const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(draft);
		if (!confirmed) return null;
		const [closed] = await firstValueFrom(this.myTimesService.update(draft)).catch(() => [null] as Time[]);
		this.isPlaying = false;
		this.resetEntites();
		return closed?.end ? closed : null;
	}

	async afterProjectRecordingClosed(result: Time | Time[] | null | undefined): Promise<void> {
		const settings = this.userSettingsQuery.getValue().workspace?.settings;
		if (!settings?.allHoursEnabled) return;
		// Drop the live dual-started WH recording; day WH is rebuilt from project times below.
		await this.clearDualStartedWorkingHoursRecording();

		const times = Array.isArray(result) ? result : result ? [result] : [];
		const closed = times.filter((t) => t?.start && t?.end);
		// Also clear matching open WH for the closed times' users (e.g. supervisor stopping another user).
		if (closed.length) {
			await this.clearDualStartedWorkingHoursForClosedProjectTimes(closed);
			// Pass the batch once; MixedTimesSyncService syncs only the start day for overnight splits.
			await this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(closed);
		}
	}
	handleError(err: HttpErrorResponse) {
		let args: any = {};
		if (err.error?.message === "errors.required") args.field = err.error.field;
		pushError(err, args);
	}
	handleSuccess(x: Time) {
		if (!x) return;
		if (x.hasTimeOverlap) this.notifyInfo("time.overlaptime_not_allow_workspace");
	}
	/**
	 * Runs the pre-stop rulebreak check, then either returns fixed entries, executes normal stop, or cancels.
	 */
	async stopComegoWithRuleCheck(
		openRecording: ComegoTime,
		executeNormalStop: () => Promise<ComegoTime[] | null>
	): Promise<{ entry: ComegoTime | null; appliedFix: boolean }> {
		const outcome = await this.comegoRecordingRulebreak.resolveBeforeStop(openRecording);
		if (outcome.action === "cancelled") return { entry: null, appliedFix: false };
		if (outcome.action === "fixed") return { entry: outcome.entries?.[0] ?? null, appliedFix: true };
		const stopped = await executeNormalStop();
		return { entry: stopped?.[0] ?? null, appliedFix: false };
	}
	resolveComegoBeforeStop(
		openRecording: ComegoTime & {
			recording?: boolean;
			userId?: string;
			type?: ComegoTime["type"] | { name?: string };
		}
	) {
		return this.comegoRecordingRulebreak.resolveBeforeStop(openRecording);
	}
	hasStopRuleViolation(
		openRecording: ComegoTime & {
			recording?: boolean;
			userId?: string;
			type?: ComegoTime["type"] | { name?: string };
		}
	) {
		return this.comegoRecordingRulebreak.hasStopRuleViolation(openRecording, { skipSessionGuard: true });
	}
	async resolveComegoStopAfterClosingParent(
		openRecording: ComegoTime & {
			recording?: boolean;
			userId?: string;
			type?: ComegoTime["type"] | { name?: string };
		}
	): Promise<BeforeStopResult> {
		const outcome = await this.comegoRecordingRulebreak.resolveBeforeStop(openRecording);
		if (outcome.action === "proceed") {
			await this.performNormalComegoStop(openRecording);
			this.notifySuccess("success.created");
		}
		return outcome;
	}

	async performNormalComegoStop(openRecording: ComegoTime): Promise<ComegoTime[] | null> {
		let end = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		if (isBefore(end, new Date(openRecording.start))) end = new Date(openRecording.start);
		const userSettings = this.userSettingsQuery.getValue();
		const targetUserId = openRecording.user?.id ?? (openRecording as ComegoTime & { userId?: string }).userId;
		const workspaceUser =
			(targetUserId && userSettings.workspace.users?.find((u) => u.id === targetUserId)) || openRecording.user;
		return this.comegoService
			.update(
				produce(openRecording, (draft) => {
					draft.end = end < new Date(draft.start) ? new Date(draft.start).toISOString() : end.toISOString();
					if (workspaceUser) draft.user = workspaceUser;
				})
			)
			.then(this.handleUserFixDialog.bind(this))
			.then(this.handleWorkingHourSuccess.bind(this))
			.catch((err: HttpErrorResponse) => {
				pushError(err);
				return Promise.reject(err);
			});
	}
	async handleUserFixDialog(times: ComegoTime[]) {
		if (!times || !times.length) return times;
		const workspace = this.userSettingsQuery.getValue().workspace;
		if (!workspace?.schedules?.workspace?.workinghours?.preventDeviatingWorkingHours) return times;
		const errorMatcher = /^errors\./;
		for (const t of times) {
			if ("_meta" in t) {
				// handle warnings
				const meta: any = t["_meta"];
				for (const [key, value] of Object.entries(meta)) {
					if (
						(typeof value === "string" && errorMatcher.test(value)) ||
						(typeof value === "object" && "message" in value)
					) {
						if (typeof value === "string") console.log("Recording error: " + value);
						else console.log("Recording error: " + value["message"]);
						const dialogRef = this.dialog.open(ComeAndGoRulebreakFixDialogComponent, {
							data: <WorkingHoursUpdateData>{
								time: {
									value: times,
									date: new Date(t.start),
									refetchFromDate: true,
								},
								forceRecreateDay: true,
								error: typeof value === "object" ? value : null,
							},
							disableClose: true,
						});
						const result = await firstValueFrom(dialogRef.afterClosed());
						if (result === null) return null;
						// clean meta after successful fix
						if ("_meta" in t && t._meta) {
							t._meta = Object.fromEntries(
								Object.entries(t._meta).filter(([_, value]) => {
									return !(
										(typeof value === "string" && errorMatcher.test(value)) ||
										(typeof value === "object" && value && "message" in value)
									);
								})
							);
						}
					}
				}
			}
		}
		return times;
	}
	async handleWorkingHourSuccess(times: ComegoTime[]) {
		if (!times || !times.length) return times;
		const errorMatcher = /^errors\./;
		times.forEach((t) => {
			if ("_meta" in t) {
				// handle warnings
				const meta: any = t["_meta"];
				Object.entries(meta).forEach(([key, value]: [string, any]) => {
					let errorCode: string;
					if (typeof value === "string" && errorMatcher.test(value)) {
						this.notifyWarn(value);
						errorCode = value;
					} else if (typeof value === "object" && "message" in value) {
						if (errorMatcher.test(value["message"])) {
							const margs = value["args"] || {};
							this.notifyWarn(value["message"], undefined, resolveRawArgs(margs));
							errorCode = value["message"];
						}
					}
				});
			}
		});
		return times;
	}
	async handleWorkingHourError(
		err: HttpErrorResponse,
		times?: ComegoTime[],
		options?: Partial<{ forceDialogError: boolean; forceUpdateTime: boolean }>
	) {
		let errorCode: string;
		const errorRaw = (errorCode = (typeof err.error === "object" && err.error?.message) || err.error);
		if (!errorCode || !/^errors\./.test(errorCode)) return;
		options = merge({}, options || {});
		const errorMessage = errorCode;
		const errorArgs =
			("args" in err.error && typeof err.error === "object" && resolveRawArgs(err.error.args)) || {};
		errorCode = errorCode.slice(errorCode.lastIndexOf(".") + 1);
		const isBreakError = isBreakRuleError(err.error);
		if (options.forceUpdateTime || options.forceDialogError || isBreakError) {
			if (!times?.length) return await Promise.reject(err);
			if (isBreakError) {
				const start = addMinutes(startOfMinute(new Date()), 1); // prevents overlap
				const end = addMinutes(start.getTime(), (err.error.args && err.error.args.minutesDue * 60) || 30);
				await this.dialog
					.open(ComeAndGoCreateDialogComponent, {
						data: <WorkingHoursCreateData>{
							initialTimes: [
								{
									start,
									end,
									type: "pause",
								},
							],
							error: {
								args: errorArgs,
								message: errorRaw,
							},
							hiddenFields: ["name"],
							disabledFields: ["name", "time.type", "time.add", "date", "user"].concat(
								options.forceDialogError ? "time" : []
							),
							allowedTimeTypes: ["pause"],
							disabled: !!options.forceDialogError,
						},
					})
					.afterClosed()
					.toPromise()
					.then(this.handleWorkingHourSuccess.bind(this))
					.catch(this.handleWorkingHourError.bind(this));
			} else
				await this.dialog
					.open(ComeAndGoCreateDialogComponent, {
						data: <WorkingHoursCreateData>{
							time: {
								value: times?.[0],
								correctTimeRanges: true,
								deleteOriginTime: true,
								lockItem: true,
							},
							error: {
								args: errorArgs,
								message: errorMessage,
							},
							hiddenFields: ["name"],
							disabledFields: ["name", "time.type", "date", "user"].concat(
								options.forceDialogError ? "time" : []
							),
							allowedTimeTypes: ["work", "pause"],
							disabled: !!options.forceDialogError,
						},
					})
					.afterClosed()
					.toPromise()
					.then(this.handleWorkingHourSuccess.bind(this))
					.catch(this.handleWorkingHourError.bind(this));
		}
		return await Promise.reject(err);
	}
	setToNow(prop: "start" | "end") {
		return this.group.patchValue({
			time: {
				[prop]: flow((x) => formatString(x, "HH:mm"))(new Date()),
			},
		});
	}
	setMinutesDiff(minutes: number, prop: "start" | "end") {
		const timespanGroup = this.group.get("time") as UntypedFormGroup;
		switch (prop) {
			case "start":
				return timespanGroup.patchValue({
					[prop]: flow(
						(x) => parseFromString(x, "HH:mm", new Date()),
						(x) => subMinutes(x, minutes),
						clampDay(new Date()),
						(x) => formatString(x, "HH:mm")
					)(timespanGroup.value.end as string),
				});
			case "end":
				return timespanGroup.patchValue({
					[prop]: flow(
						(x) => parseFromString(x, "HH:mm", new Date()),
						(x) => addMinutes(x, minutes),
						clampDay(new Date()),
						(x) => formatString(x, "HH:mm")
					)(timespanGroup.value.start as string),
				});
		}
	}
	setHourDiff(hours: number, prop: "start" | "end") {
		const timespanGroup = this.group.get("time") as UntypedFormGroup;
		switch (prop) {
			case "start":
				return timespanGroup.patchValue({
					[prop]: flow(
						(x) => parseFromString(x, "HH:mm", new Date()),
						(x) => subHours(x, hours),
						clampDay(new Date()),
						(x) => formatString(x, "HH:mm")
					)(timespanGroup.value.end as string),
				});
			case "end":
				return timespanGroup.patchValue({
					[prop]: flow(
						(x) => parseFromString(x, "HH:mm", new Date()),
						(x: Date) => addHours(x, hours),
						clampDay(new Date()),
						(x) => formatString(x, "HH:mm")
					)(timespanGroup.value.start as string),
				});
		}
	}
	setWorkDay() {
		this.group.patchValue({
			time: {
				start: "09:00",
				end: "17:00",
			},
		});
	}
}
const log = new Logger(RecordToolbarService.name);
